import { useCallback, useEffect, useRef } from "react";
import type { RefObject } from "react";
import type { KarelIdeHandle } from "../../ide/KarelIdeComponent";
import { useThemeStore, type LanguageMode } from "../../app/stores/themeStore";
import { fetchStarterCode } from "../../util/starterCodeLoader";

interface UseIdeStarterCodeOptions {
  /** Called before the starter code begins loading for a new lesson. */
  onWillLoad?: () => void;
  /** Called once the starter code (or the user's saved code) is in the editor. */
  onLoaded?: () => void;
}

interface UseIdeStarterCodeResult {
  /** Reloads the current language's starter code, discarding saved edits. */
  reset: () => void;
  /** Jumps to the end of the streaming animation, making the IDE ready. */
  skip: () => void;
}

/**
 * Keeps a separate copy of the editor contents per language. Switching
 * languages restores that language's edits (or loads its starter code the first
 * time), so a student's JS and Python programs don't clobber each other.
 */
export function useIdeStarterCode(
  starterCode: string,
  ideRef: RefObject<KarelIdeHandle | null>,
  options: UseIdeStarterCodeOptions = {},
): UseIdeStarterCodeResult {
  const languageMode = useThemeStore((s) => s.languageMode);
  const previousLanguageRef = useRef<LanguageMode>(languageMode);
  const codeByLanguageRef = useRef<Partial<Record<LanguageMode, string>>>({});
  const starterCodeCacheRef = useRef<Partial<Record<LanguageMode, string>>>({});
  const loadedLanguageRef = useRef<LanguageMode | null>(null);
  const initialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const clearInitialTimer = useCallback((): void => {
    if (initialTimerRef.current !== null) {
      clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
    }
  }, []);

  const loadStarter = useCallback(
    (language: LanguageMode, animate: boolean) => {
      const requestId = ++requestIdRef.current;
      const finish = (): void => {
        if (requestId !== requestIdRef.current) return;
        loadedLanguageRef.current = language;
        optionsRef.current.onLoaded?.();
      };

      const cached = starterCodeCacheRef.current[language];
      if (cached !== undefined) {
        if (animate) {
          ideRef.current?.animateCode(cached, finish);
        } else {
          ideRef.current?.setCode(cached);
          finish();
        }
        return;
      }

      void fetchStarterCode(starterCode, language)
        .then((code) => {
          if (requestId !== requestIdRef.current) return;
          starterCodeCacheRef.current[language] = code;
          if (animate) {
            ideRef.current?.animateCode(code, finish);
          } else {
            ideRef.current?.setCode(code);
            finish();
          }
        })
        .catch(() => finish());
    },
    [starterCode, ideRef],
  );

  useEffect(() => {
    codeByLanguageRef.current = {};
    starterCodeCacheRef.current = {};
    loadedLanguageRef.current = null;
    previousLanguageRef.current = useThemeStore.getState().languageMode;
    optionsRef.current.onWillLoad?.();
    initialTimerRef.current = setTimeout(() => {
      initialTimerRef.current = null;
      loadStarter(useThemeStore.getState().languageMode, true);
    }, 600);
    return clearInitialTimer;
  }, [starterCode, loadStarter, clearInitialTimer]);

  useEffect(() => {
    const previous = previousLanguageRef.current;
    if (previous === languageMode) return;
    previousLanguageRef.current = languageMode;
    clearInitialTimer();

    if (loadedLanguageRef.current === previous) {
      const editor = ideRef.current?.getEditor();
      if (editor) codeByLanguageRef.current[previous] = editor.getValue();
    }

    const saved = codeByLanguageRef.current[languageMode];
    if (saved !== undefined) {
      requestIdRef.current += 1;
      loadedLanguageRef.current = languageMode;
      ideRef.current?.setCode(saved);
    } else {
      loadStarter(languageMode, false);
    }
  }, [languageMode, ideRef, loadStarter, clearInitialTimer]);

  const reset = useCallback(() => {
    const language = useThemeStore.getState().languageMode;
    clearInitialTimer();
    delete codeByLanguageRef.current[language];
    requestIdRef.current += 1;
    loadStarter(language, false);
  }, [clearInitialTimer, loadStarter]);

  const skip = useCallback(() => {
    const language = useThemeStore.getState().languageMode;
    clearInitialTimer();
    requestIdRef.current += 1;
    loadStarter(language, false);
  }, [clearInitialTimer, loadStarter]);

  return { reset, skip };
}
